import React, { useMemo, useState } from 'react';
import {
  Alert, Breadcrumb, Button, Card, Checkbox, Col, Empty, Form, Input, InputNumber, Modal,
  Popconfirm, Row, Select, Space, Spin, Statistic, Typography, message,
} from 'antd';
import { DeleteOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import MainLayout from '../../components/common/MainLayout';
import { useListSavesQuery } from '../../api/savesV2Api';
import {
  useArchiveGardenPlanMutation,
  useCaptureGardenPlanMutation,
  useListGardenPlansQuery,
} from '../../api/gardenPlansApi';

const { Paragraph, Text, Title } = Typography;

interface CaptureValues {
  sourceSaveId: string;
  name: string;
  description?: string;
  sourceCalendarYear: number;
  includePlantings?: boolean;
}

const GardenPlansPage: React.FC = () => {
  const [captureOpen, setCaptureOpen] = useState(false);
  const [form] = Form.useForm<CaptureValues>();
  const { data: plans = [], isLoading, isError, refetch } = useListGardenPlansQuery({ status: 'active' });
  const { data: saves = [], isFetching: savesLoading } = useListSavesQuery({ type: 'free', status: 'active', limit: 100 });
  const [capture, { isLoading: capturing }] = useCaptureGardenPlanMutation();
  const [archive, { isLoading: archiving }] = useArchiveGardenPlanMutation();

  const saveOptions = useMemo(() => saves.map((save) => ({
    value: save.id,
    label: save.farmName || save.id,
  })), [saves]);

  const openCapture = () => {
    form.setFieldsValue({ sourceCalendarYear: new Date().getFullYear(), includePlantings: false });
    setCaptureOpen(true);
  };

  const submitCapture = async () => {
    try {
      const values = await form.validateFields();
      const { includePlantings, ...captureValues } = values;
      const result = await capture({
        ...captureValues,
        layers: { topology: true, plantings: includePlantings === true },
      }).unwrap();
      message.success(result.idempotent ? 'Такая версия уже сохранена' : 'План сохранён');
      if (result.warnings?.length) message.warning(`Capture завершён с предупреждениями: ${result.warnings.length}`);
      form.resetFields();
      setCaptureOpen(false);
    } catch (error) {
      const apiError = error as { data?: { message?: string }; errorFields?: unknown };
      if (!apiError.errorFields) message.error(apiError.data?.message || 'Не удалось сохранить план');
    }
  };

  const archivePlan = async (planId: string) => {
    try {
      await archive(planId).unwrap();
      message.success('План перемещён в архив');
    } catch (error) {
      const apiError = error as { data?: { message?: string } };
      message.error(apiError.data?.message || 'Не удалось архивировать план');
    }
  };

  return (
    <MainLayout breadcrumb={<Breadcrumb items={[{ title: <Link to="/">Главная</Link> }, { title: 'Планы' }]} />}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
        <Title level={3} style={{ margin: 0 }}>Планы сада</Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()}>Обновить</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCapture}>Сохранить из сейва</Button>
        </Space>
      </Space>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="План — неизменяемый снимок раскладки"
        description="Его можно применять в следующих сезонах. В первой реализации сохраняется топология; схема посадок будет подключена следующим слоем."
      />

      {isError && <Alert type="error" message="Не удалось загрузить планы" style={{ marginBottom: 16 }} />}
      {isLoading ? <div style={{ textAlign: 'center', padding: 48 }}><Spin size="large" /></div> : plans.length === 0 ? (
        <Empty description="Сохранённых планов пока нет">
          <Button type="primary" onClick={openCapture}>Сохранить первый план</Button>
        </Empty>
      ) : (
        <Row gutter={[16, 16]}>
          {plans.map((plan) => {
            const counts = plan.latestVersionSummary?.counts;
            return (
              <Col key={plan.id} xs={24} md={12} xl={8}>
                <Card
                  title={plan.name}
                  extra={(
                    <Popconfirm title="Архивировать план?" onConfirm={() => archivePlan(plan.id)}>
                      <Button danger type="text" icon={<DeleteOutlined />} loading={archiving} />
                    </Popconfirm>
                  )}
                >
                  {plan.description && <Paragraph ellipsis={{ rows: 2 }}>{plan.description}</Paragraph>}
                  <Row gutter={12}>
                    <Col span={8}><Statistic title="Поля" value={counts?.fields ?? 0} /></Col>
                    <Col span={8}><Statistic title="Участки" value={counts?.plots ?? 0} /></Col>
                    <Col span={8}><Statistic title="Грядки" value={counts?.beds ?? 0} /></Col>
                  </Row>
                  <Space direction="vertical" size={2} style={{ marginTop: 12 }}>
                    <Text type="secondary">Версия: {plan.latestVersionId ? 'latest' : '—'}</Text>
                    <Text type="secondary">Масштаб: {plan.latestVersionSummary?.planScale?.preset || '—'}</Text>
                  </Space>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}

      <Modal
        title="Сохранить план из сейва"
        open={captureOpen}
        onCancel={() => setCaptureOpen(false)}
        onOk={submitCapture}
        confirmLoading={capturing}
        okText="Сохранить"
        cancelText="Отмена"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="sourceSaveId" label="Сейв-источник" rules={[{ required: true, message: 'Выберите сейв' }]}>
            <Select loading={savesLoading} options={saveOptions} showSearch optionFilterProp="label" />
          </Form.Item>
          <Form.Item name="name" label="Название плана" rules={[{ required: true, message: 'Введите название' }, { max: 160 }]}>
            <Input />
          </Form.Item>
          <Form.Item name="sourceCalendarYear" label="Календарный год" rules={[{ required: true }]}>
            <InputNumber min={1900} max={2200} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="description" label="Описание"><Input.TextArea rows={3} /></Form.Item>
          <Form.Item name="includePlantings" valuePropName="checked">
            <Checkbox>Включить годовую схему посадок</Checkbox>
          </Form.Item>
          <Alert type="info" showIcon message="Переносятся намерения, а не состояние растений" />
        </Form>
      </Modal>
    </MainLayout>
  );
};

export default GardenPlansPage;
